import React from "react";
import ModalWidget from "../ModalWidget";
import { ReactComponent as ImportBorderSvg } from "../../../assets/svg/import.svg";
import type { ImportSummary as ImportSummaryType } from "../../../services/UserService";

interface ImportSummaryProps {
    summary: ImportSummaryType;
    onDone: () => void;
    onClose?: () => void;
}

function ImportSummary({ summary, onDone, onClose }: ImportSummaryProps) {
    const { created, skipped, errors, warnings = [] } = summary;
    const total = created + skipped;

    return (
        <ModalWidget
            headerIcon={ImportBorderSvg}
            show={true}
            onHide={onClose}
            title="Import Complete"
            description={`${created} of ${total} user${total !== 1 ? "s" : ""} imported successfully.`}
            actionTitle="Done"
            onAction={onClose ?? onDone}
            navigateBack={false}
            hasBack={false}
        >
            <div className="d-flex gap-3 mb-3">
                <div className="p-3 text-center flex-fill" style={{ backgroundColor: "#f0fdf4", borderRadius: 8 }}>
                    <div className="font-bold" style={{ fontSize: "1.5rem", color: "#16a34a" }}>
                        {created}
                    </div>
                    <div className="font-xs text-muted">Created</div>
                </div>
                <div className="p-3 text-center flex-fill" style={{ backgroundColor: "#fefce8", borderRadius: 8 }}>
                    <div className="font-bold" style={{ fontSize: "1.5rem", color: "#ca8a04" }}>
                        {skipped}
                    </div>
                    <div className="font-xs text-muted">Skipped</div>
                </div>
                {errors.length > 0 && (
                    <div className="p-3 text-center flex-fill" style={{ backgroundColor: "#fef2f2", borderRadius: 8 }}>
                        <div className="font-bold" style={{ fontSize: "1.5rem", color: "#dc2626" }}>
                            {errors.length}
                        </div>
                        <div className="font-xs text-muted">Errors</div>
                    </div>
                )}
                {warnings.length > 0 && (
                    <div className="p-3 text-center flex-fill" style={{ backgroundColor: "#fffbeb", borderRadius: 8 }}>
                        <div className="font-bold" style={{ fontSize: "1.5rem", color: "#d97706" }}>
                            {warnings.length}
                        </div>
                        <div className="font-xs text-muted">Warnings</div>
                    </div>
                )}
            </div>

            {errors.length > 0 && (
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    <p className="font-xs font-bold mb-2">Error Details</p>
                    {errors.map((err, idx) => (
                        <div key={idx} className="d-flex gap-2 mb-1">
                            <span className="font-xs text-muted" style={{ minWidth: 50 }}>
                                Row {err.row}
                            </span>
                            <span className="font-xs text-danger">{err.message}</span>
                        </div>
                    ))}
                </div>
            )}

            {warnings.length > 0 && (
                <div style={{ maxHeight: 200, overflowY: "auto" }} className={errors.length > 0 ? "mt-3" : ""}>
                    <p className="font-xs font-bold mb-2">Warning Details</p>
                    {warnings.map((warn, idx) => (
                        <div key={idx} className="d-flex gap-2 mb-1">
                            <span className="font-xs text-muted" style={{ minWidth: 50 }}>
                                Row {warn.row}
                            </span>
                            <span className="font-xs" style={{ color: "#d97706" }}>{warn.message}</span>
                        </div>
                    ))}
                </div>
            )}
        </ModalWidget>
    );
}

export default ImportSummary;
